import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { areComponentsEqual } from 'react-hot-loader';
import {
    CSSTransition,
    TransitionGroup,
} from 'react-transition-group';

import styles from './SlideInOut.scss';

const transitionClasses = {
    enter: styles.enter,
    enterActive: styles.enterActive,
    enterDone: styles.enterDone,
    exit: styles.exit,
    exitActive: styles.exitActive,
    exitDone: styles.exitDone,
};

const reverseTransitionClasses = {
    enter: styles.reverseEnter,
    enterActive: styles.reverseEnterActive,
    enterDone: styles.reverseEnterDone,
    exit: styles.reverseExit,
    exitActive: styles.reverseExitActive,
    exitDone: styles.reverseExitDone,
};

function SlideInOut(props) {
    function _shouldShow() {
        const {
            in: transitionIn,
            show,
        } = props;

        if (typeof show !== 'undefined') {
            return show;
        }

        return transitionIn;
    }

    const {
        children,
        className,
        _className,
        duration,
        isReverse,
        ...transitionProps
    } = props;

    return (
        <CSSTransition
            {...transitionProps}
            classNames={isReverse
                ? reverseTransitionClasses
                : transitionClasses}
            in={_shouldShow()}
            timeout={duration}
        >
            {(transitionState) => {
                const hasExited = transitionState === 'exited';
                const hasEntered = transitionState === 'entered';
                const isEntering = transitionState === 'entering';
                const transitionStyles = {
                    transitionDuration: `${duration / 2}ms`,
                    transitionDelay: isEntering
                        ? `${duration}ms`
                        : '0ms',
                };

                return (
                    <div
                        className={classNames(
                            _className,
                            className,
                            {
                                [styles.slide]: !isReverse,
                                [styles.reverseSlide]: isReverse,
                                [styles.visible]: hasEntered,
                                [styles.hidden]: hasExited,
                            },
                        )}
                        style={transitionStyles}
                    >
                        {children}
                    </div>
                );
            }}
        </CSSTransition>
    );
}

SlideInOut.propTypes = {
    duration: PropTypes.number,
    _className: PropTypes.string,
    className: PropTypes.string,
    show: PropTypes.bool,
    isReverse: PropTypes.bool,
};

SlideInOut.defaultProps = {
    duration: 500,
    isReverse: false,
};

function SlideInOutContainer(props) {
    function _augmentChildren(children) {
        const {
            duration,
        } = props;

        return React.Children.map(children, (child) => {
            if (child && areComponentsEqual(child.type, SlideInOut)) {
                return React.cloneElement(child, {
                    _className: props.childClassName,
                    duration: duration,
                });
            }

            return child;
        });
    }

    const {
        className,
    } = props;

    return (
        <TransitionGroup
            className={classNames(className, styles.container)}
        >
            {_augmentChildren(props.children)}
        </TransitionGroup>
    );
}

SlideInOutContainer.propTypes = {
    className: PropTypes.string,
    childClassName: PropTypes.string,
    duration: PropTypes.number,
};

SlideInOutContainer.defaultProps = {
    className: '',
    childClassName: '',
    duration: 500,
};

export {
    SlideInOut,
    SlideInOutContainer,
};
